import React, { useState } from "react";
import { ScriptMessageCardProps } from "./ScriptMessageCard.types";
import {
  ScriptContentArea,
  StyledScriptText,
  ExpandButton,
} from "./ScriptMessageCard.styles";
import { Icon } from "../../../components/Icon/Icon";
import { colorPalette } from "../../../theme/colors";
import FlexColumn from "../../../components/Layout/FlexColumn/FlexColumn";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";
import {
  Body5RegularTypography,
  Body6RegularTypography,
  Caps2SemiboldTypography,
} from "../../../primitives/Typography/typographyVariants";
import Tags from "../../../components/Tags/Tags";
import { Button } from "../../../components/Button/Button";
import Box from "../../../primitives/Box/Box";

export const ScriptMessageCard: React.FC<ScriptMessageCardProps> = ({
  title = "SCRIPT SUBMISSION",
  versionStatus,
  content,
  status,
  timestamp,
  isEditable = false,
  isActionCompleted = false,
  onEdit,
  dataCy = "script-message-card",
  showTimestamp = true,
  variant = "default",
  onClick,
  isFeedback = false,
  maxHeight,
  subtitle,
  onReferenceClick,
  isReferenceLoading = false,
  width,
  maxWidth,
  onExpand,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleEdit = () => {
    if (!isEditable) return;
    onEdit?.();
  };

  // Different character limits for feedback vs normal content
  const characterLimit = isFeedback ? 100 : 500;
  const shouldShowExpandButton = content.length > characterLimit;
  // For normal content, always show full content
  // For feedback content, show truncated content with expand option
  // Show truncated or full content based on expand state
  const displayContent =
    isExpanded || !shouldShowExpandButton || variant === "scrollable"
      ? content
      : content.substring(0, characterLimit);

  return (
    <FlexColumn
      data-cy={dataCy}
      backgroundColor={colorPalette.popWhite[300]}
      borderRadius="8px"
      pt={12}
      pb={4}
      px={12}
      gap={8}
      onClick={onClick}
      width={width}
      maxWidth={maxWidth}
    >
      {isReferenceLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          borderRadius="8px"
          backgroundColor="rgba(255, 255, 255, 0.9)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={10}
        >
          <FlexColumn alignItems="center" gap={8}>
            <Icon
              name="fa-solid fa-spinner"
              size="24"
              height="24"
              width="24"
              color={colorPalette.popOrange[500]}
              className="fa-spin"
            />
            <Body6RegularTypography color={colorPalette.popBlack[400]}>
              Finding message...
            </Body6RegularTypography>
          </FlexColumn>
        </Box>
      )}
      {title && (
        <Caps2SemiboldTypography
          fontFamily="Lexend"
          color={colorPalette.popBlack[100]}
          className="message-card-title"
        >
          {title}
        </Caps2SemiboldTypography>
      )}
      {versionStatus && (
        <Tags
          type="track-small"
          label={versionStatus.label}
          icon={versionStatus.icon}
          backgroundColor={versionStatus.backgroundColor}
          color={versionStatus.textColor}
          iconColor={versionStatus.iconColor}
        />
      )}

      {/* Content Area */}
      <ScriptContentArea
        variant={variant}
        maxHeight={maxHeight}
        isFeedback={isFeedback}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onReferenceClick?.();
          }
        }}
      >
        <StyledScriptText
          dangerouslySetInnerHTML={{ __html: displayContent }}
          data-cy="script-content"
          isFeedback={isFeedback}
        />

        {/* Inline expand button for feedback content */}
        {shouldShowExpandButton && variant !== "scrollable" && (
          <>
            <ExpandButton
              onClick={(e) => {
                e.stopPropagation(); // Prevent event from bubbling to parent
                setIsExpanded(!isExpanded);
                onExpand?.();
              }}
              data-cy="expand-button"
            >
              {isExpanded ? "show less" : "show more"}
            </ExpandButton>
          </>
        )}
      </ScriptContentArea>

      {subtitle && (
        <Body5RegularTypography color={colorPalette.popBlack[500]}>
          {subtitle}
        </Body5RegularTypography>
      )}

      {(status || isEditable) && (
        <FlexRow justifyContent={status ? "space-between" : "flex-end"} py={4}>
          {status ? (
            <Tags
              type="collab-status"
              label={status.label}
              color={status.textColor}
              backgroundColor={status.backgroundColor}
              icon={status.icon}
              iconColor={status.iconColor}
            />
          ) : (
            <></>
          )}
          {isEditable && (
            <Button
              variant="secondary"
              onClick={handleEdit}
              size="small"
              width="auto"
              disabled={isActionCompleted}
              leftIcon={
                <FlexColumn
                  alignItems="center"
                  justifyContent="center"
                  height="26px"
                  color="inherit"
                >
                  <Icon
                    name="fa-solid fa-pen-to-square"
                    size="12"
                    height="18"
                    width="18"
                    color="inherit"
                  />
                </FlexColumn>
              }
            >
              Edit Script
            </Button>
          )}
        </FlexRow>
      )}

      {/* Timestamp */}
      {showTimestamp && timestamp && (
        <FlexRow justifyContent="flex-end">
          <Body6RegularTypography
            data-cy="timestamp"
            color={colorPalette.popBlack[100]}
          >
            {timestamp}
          </Body6RegularTypography>
        </FlexRow>
      )}
    </FlexColumn>
  );
};
